/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ConfigurationVariables } from "../config";
import { getBearerToken, getIAMProjectID } from "../utils/utility";
import { AD_ORGANIZATION_ID, RBM_ACCESS_TOKEN_STORAGE_NAME } from "../utils/constants";

export const softwareAPI = createApi({
  reducerPath: "softwareAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: `${ConfigurationVariables.API_BASE_HOST_URL}`,
    prepareHeaders: async (headers) => {
      try {
        headers.set("authorization", await getBearerToken(RBM_ACCESS_TOKEN_STORAGE_NAME));
        headers.set("organizationId", AD_ORGANIZATION_ID);
        headers.set("IAM-ProjectId", getIAMProjectID());
      } catch (error) {
        console.error(error);
      }
      return headers;
    },
  }),
  tagTypes: ["Software", "SoftwareLogs", "SoftwareVersions"],
  endpoints: (builder) => ({
    // Get list of edge apps for a gateway
    getSoftwareList: builder.query({
      query: ({ gatewayId }) =>
        `v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/edgeapps`,
      providesTags: ["Software"]
    }),
    // Install application on gateway
    installSoftware: builder.mutation({
      query: ({ gatewayId, appKey, formData }) => {
        const body = formData?.version !== undefined
          ? { version: formData.version, attributes: formData.attributes || {} }
          : { attributes: formData || {} };
        return {
          url: `v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/app/${appKey}`,
          method: "POST",
          body
        };
      },
      invalidatesTags: ["Software"]
    }),
    // Uninstall application from gateway
    uninstallSoftware: builder.mutation({
      query: ({ gatewayId, appKey }) => ({
        url: `v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/app/${appKey}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Software"]
    }),
    // Reinstall application on gateway
    reinstallSoftware: builder.mutation({
      query: ({ gatewayId, appKey, formData }) => {
        const body = formData?.version !== undefined
          ? { version: formData.version, attributes: formData.attributes || {} }
          : { attributes: formData || {} };
        return {
          url: `v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/app/${appKey}`,
          method: "PUT",
          body
        };
      },
      invalidatesTags: ["Software"]
    }),
    // Get application logs
    getSoftwareLogs: builder.query({
      query: ({ gatewayId, appKey }) => ({
        url: `v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/app/${appKey}/logs`,
        method: "GET"
      }),
      providesTags: ["SoftwareLogs"]
    }),
    // Get installed app details
    getSoftwareDetails: builder.query({
      query: ({ gatewayId, appKey }) => 
        `/v2/projects/${getIAMProjectID()}/gateway/${gatewayId}/app/${appKey}`,
    }),

    //Might delete later
    getTargetForgePoints: builder.query({
      query: ({ softwareId, softwareKey }) => 
        `/v2/projects/${getIAMProjectID()}/software/${softwareId}/forge-points?key=${softwareKey}`
    })
  })
});

export const {
  useGetSoftwareListQuery,
  useInstallSoftwareMutation,
  useUninstallSoftwareMutation,
  useReinstallSoftwareMutation,
  useLazyGetSoftwareLogsQuery,
  useLazyGetSoftwareDetailsQuery,
  useGetTargetForgePointsQuery
} = softwareAPI;